import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, getDocs, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  MagnifyingGlassIcon, 
  ArrowUpTrayIcon, 
  EyeIcon, 
  EyeSlashIcon, 
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { QueueListIcon, Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';

export default function Discovery() {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchOnMobile, setShowSearchOnMobile] = useState(false);
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // Default to grid, will be updated in useEffect
  const searchInputRef = useRef(null);

  useEffect(() => {
    async function fetchPublicContents() {
      try {
        const q = query(
          collection(db, 'contents'),
          where('isPublic', '==', true),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const publicContents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setContents(publicContents);
      } catch (error) {
        console.error('Error fetching public contents:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchPublicContents();
  }, []);

  useEffect(() => {
    async function checkAdminStatus() {
      if (!currentUser) return;
      
      try {
        const adminDocRef = doc(db, 'admins', currentUser.email);
        const adminDoc = await getDoc(adminDocRef);
        
        setIsAdmin(adminDoc.exists() && adminDoc.data().isAdmin === "true");
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    }

    checkAdminStatus();
  }, [currentUser]);

  useEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Set viewMode based on mobile status and localStorage
      const storedViewMode = localStorage.getItem('viewMode');
      if (mobile) {
        setViewMode('grid');
      } else if (storedViewMode) {
        setViewMode(storedViewMode);
      } else {
        setViewMode('list'); // Default for desktop
      }
    };
    
    // Initial check
    checkIfMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const handleVisibilityToggle = async (contentId, currentVisibility) => {
    try {
      await updateDoc(doc(db, 'contents', contentId), {
        isPublic: !currentVisibility
      });
      setContents(prevContents =>
        prevContents.map(content =>
          content.id === contentId
            ? { ...content, isPublic: !currentVisibility }
            : content
        )
      );
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const handleDelete = async (contentId) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await deleteDoc(doc(db, 'contents', contentId));
      setContents(contents.filter(content => content.id !== contentId));
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const handleViewModeToggle = () => {
    if (!isMobile) {
      const newMode = viewMode === 'grid' ? 'list' : 'grid';
      setViewMode(newMode);
      localStorage.setItem('viewMode', newMode);
    }
  };

  const filteredContents = contents.filter(content => 
    content.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close search when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (showSearchOnMobile && searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchOnMobile(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchOnMobile]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header Section - Responsive Layout */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 sm:mb-8 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold">Discover Learning Content</h1>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {/* Search Bar - Integrated in header */}
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search by title or style..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            
            {/* View Mode Toggle */}
            <div className="flex gap-2 justify-between sm:justify-start">
              <button
                onClick={handleViewModeToggle}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${
                  isMobile ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-label={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
                disabled={isMobile}
              >
                {viewMode === 'grid' ? (
                  <>
                    <ListBulletIcon className="h-5 w-5" />
                    <span className="hidden sm:inline">List View</span>
                  </>
                ) : (
                  <>
                    <Squares2X2Icon className="h-5 w-5" />
                    <span className="hidden sm:inline">Grid View</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-xl text-gray-500 dark:text-gray-400">Loading...</span>
          </div>
        ) : filteredContents.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-xl">No content found</p>
            {searchTerm && (
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                Try adjusting your search terms
              </p>
            )}
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6' : 'space-y-4'}>
            {filteredContents.map((content) => (
              <div 
                key={content.id} 
                className={`bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all ${
                  viewMode === 'grid' ? 'p-4 sm:p-6' : 'p-3 sm:p-4'
                } relative ${
                  viewMode === 'list' ? 'flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4' : ''
                } cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700`}
                onClick={(e) => {
                  // Check if the click originated from a control button
                  if (
                    e.target.closest('.control-button')
                  ) {
                    // Don't navigate if a control button was clicked
                    return;
                  }
                  
                  // Navigate based on content type
                  if (content.externalUrl) {
                    setContents(prevContents =>
                      prevContents.map(c =>
                        c.id === content.id ? { ...c, isExpanded: true } : c
                      )
                    );
                  } else {
                    window.location.href = `/viewer/${content.id}`;
                  }
                }}
              >
                {/* Thumbnail for list view - Responsive */}
                {(content.originalUrl?.includes('youtube.com') || content.originalUrl?.includes('youtu.be')) && viewMode === 'list' && (
                  <div className="w-full sm:w-48 aspect-video rounded overflow-hidden shadow-sm mb-3 sm:mb-0">
                    <img
                      src={`https://img.youtube.com/vi/${
                        content.originalUrl?.includes('youtu.be') 
                          ? content.originalUrl.split('/').pop().split('?')[0] 
                          : (content.originalUrl.match(/[?&]v=([^&]+)/)?.[1] || '') 
                      }/mqdefault.jpg`}
                      alt={content.title}
                      className="w-full h-full object-cover transition-transform hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                {!content.originalUrl?.includes('youtube.com') && !content.originalUrl?.includes('youtu.be') && viewMode === 'list' && (
                  <div className="w-full sm:w-48 aspect-video rounded overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-sm mb-3 sm:mb-0">
                    <div className="flex flex-col items-center space-y-1">
                      <span className="text-white font-medium">{content.writingStyle || 'Content'}</span>
                      <span className="text-white/70 text-sm">Custom Content</span>
                    </div>
                  </div>
                )}

                {/* Thumbnail for grid view - Responsive */}
                {viewMode === 'grid' && (
                  <div className="mb-3 sm:mb-4">
                    {(content.originalUrl?.includes('youtube.com') || content.originalUrl?.includes('youtu.be')) ? (
                      <div className="w-full aspect-video rounded overflow-hidden shadow-sm">
                        <img
                          src={`https://img.youtube.com/vi/${
                            content.originalUrl?.includes('youtu.be') 
                              ? content.originalUrl.split('/').pop().split('?')[0] 
                              : (content.originalUrl.match(/[?&]v=([^&]+)/)?.[1] || '') 
                          }/mqdefault.jpg`}
                          alt={content.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-full aspect-video rounded overflow-hidden bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-sm">
                        <div className="flex flex-col items-center space-y-1">
                          <span className="text-white font-medium">{content.writingStyle || 'Content'}</span>
                          <span className="text-white/70 text-sm">Custom Content</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg sm:text-xl font-semibold truncate group relative text-gray-900 dark:text-white" title={content.title}>
                        <span className="truncate block">{content.title || 'Untitled'}</span>
                        <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-purple-400 group-hover:w-full transition-all duration-300"></span>
                      </h2>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click event
                          handleVisibilityToggle(content.id, content.isPublic);
                        }}
                        className={`control-button flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                          content.isPublic 
                            ? 'text-green-600 dark:text-green-400 hover:text-green-500 dark:hover:text-green-300 hover:bg-green-400/10' 
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-400/10'
                        }`}
                        title={content.isPublic ? 'Make Private' : 'Make Public'}
                        aria-label={content.isPublic ? 'Make Private' : 'Make Public'}
                      >
                        {content.isPublic ? (
                          <EyeIcon className="h-5 w-5" />
                        ) : (
                          <EyeSlashIcon className="h-5 w-5" />
                        )}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card click event
                            handleDelete(content.id);
                          }}
                          className="control-button flex items-center justify-center w-10 h-10 rounded-full text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-400/10 transition-colors"
                          title="Delete content"
                          aria-label="Delete content"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col space-y-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      {content.writingStyle && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-400/10 text-purple-800 dark:text-purple-400">
                          {content.writingStyle}
                        </span>
                      )}
                      {content.language && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 dark:bg-teal-400/10 text-teal-800 dark:text-teal-400">
                          {content.language === 'en' ? 'English' :
                           content.language === 'zh' ? '中文' :
                           content.language === 'es' ? 'Español' :
                           content.language === 'fr' ? 'Français' :
                           content.language === 'de' ? 'Deutsch' :
                           content.language === 'it' ? 'Italiano' :
                           content.language === 'pt' ? 'Português' :
                           content.language === 'ru' ? 'Русский' :
                           content.language === 'ja' ? '日本語' :
                           content.language === 'ko' ? '한국어' :
                           content.language || 'unknown'}
                        </span>
                      )}
                      <span className="text-gray-500 text-xs">
                        {new Date(content.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      {content.externalUrl && content.isExpanded ? (
                        <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col" onClick={(e) => e.stopPropagation()}>
                          <div className="bg-gray-100 dark:bg-gray-800 p-4 flex items-center gap-4 shadow-lg">
                            <button
                              onClick={() => {
                                setContents(prevContents =>
                                  prevContents.map(c =>
                                    c.id === content.id ? { ...c, isExpanded: false } : c
                                  )
                                );
                              }}
                              className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                              </svg>
                              <span>Back</span>
                            </button>
                            <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">{content.title || 'Untitled'}</h2>
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <iframe
                              src={content.externalUrl}
                              className="w-full h-full"
                              frameBorder="0"
                              title={content.title}
                            />
                          </div>
                        </div>
                      ) : (
                        <Link
                          to={!content.externalUrl ? `/viewer/${content.id}` : '#'}
                          className={!content.externalUrl ? "inline-flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 transition-colors group mt-2" : "hidden"}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span>View Content</span>
                          <ArrowUpTrayIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* No results found state */}
        {!loading && filteredContents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-1">No content found</h3>
            {searchTerm && (
              <p className="text-gray-500 dark:text-gray-400">
                Try adjusting your search terms
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}